import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import sortBy from "lodash/sortBy";

import { TableHeaderSort } from "../../components/Table/components/TableHeader/types";
import {
  IOfferingPayload,
  Offering as OfferingType,
  SKUWrapper,
} from "../../services/api/offering/types";
import {
  fetchDataTrigger,
  resetAll,
  selectError,
  selectIsFetching,
  selectOfferingWithDefaultProp,
  selectPage,
  selectPages,
  selectSort,
  updatePagination,
  updateSort,
} from "../../store/offerings";
import OfferingsTable from "./components/OfferingsTable";
import FilterSection from "./components/FilterSection";
import OfferingModal from "./components/OfferingModal";
import toaster from "../../services/toaster";
import Pagination from "../../components/Pagination";
import ButtonPrimary from "../../components/ButtonPrimary";
import ExpandedRow from "./components/ExpandedRow";
import Message from "../../components/Table/components/Message";
import ProductLineAPIService from "../../services/api/productLine";
import offeringService from "../../services/api/offering";
import { ACTION, SUBJECT } from "../../constants/permissions";
import Can from "../../components/Can";
import Toaster from "../../services/toaster";
import {
  fetchProductLinesRequest,
  selectProductLines,
  selectTenantCode,
} from "../../store/metadata";
import { ISKU } from "../../store/skuMapping";
import { ISKUResponse } from "../../services/api/SKU/types";
import SKUAPIService from "../../services/api/SKU";
import { ISKUParams } from "./components/OfferingModal/types";
import { PROBLEM_TYPE_SKU_BEING_TO_ANOTHER_OFFERING } from "./constants";
import { ContentLoaderContainer } from "../../components/Loader/ContentLoader/ContentLoaderContainer";
import { validationRules } from "../../utils/common/contants";
import { useDialog } from "../../hooks";
import { ITableDataParams } from "../../components/Table/types";
import { ConfirmModal } from "../../components/ConfirmModal/ConfirmModal";

import "./styles.scss";

export const Offering: FC = () => {
  const { t } = useTranslation();
  const updateOfferingText = t("updateOffering");

  const dispatch = useDispatch();
  const data = useSelector(selectOfferingWithDefaultProp);
  const currentPage = useSelector(selectPage);
  const pages = useSelector(selectPages);
  const defaultSort = useSelector(selectSort);
  const isFetching = useSelector(selectIsFetching);
  const error = useSelector(selectError);
  const productLines = useSelector(selectProductLines);
  const tenantCode = useSelector(selectTenantCode);
  const [
    isShowConfirmCreateModal,
    openConfirmCreateModal,
    closeConfirmCreateModal,
  ] = useDialog();
  const [
    isShowConfirmEditModal,
    openConfirmEditModal,
    closeConfirmEditModal,
  ] = useDialog();

  const [currentOffer, setCurrentOffer] = useState(null as any);
  const [currentEditedOffer, setCurrentEditedOffer] = useState(null as any);
  const [showNewOfferingModal, setShowNewOfferingModal] = useState(false);
  const [showEditOfferingModal, setShowEditOfferingModal] = useState(false);
  const [SKUList, setSKUList] = useState([] as ISKU[]);
  const [showDefaultOfferingModal, showShowDefaultOfferingModal] = useState(
    false,
  );
  const [isFetchingCreateOffering, setIsFetchingCreateOffering] = useState(
    false,
  );
  const [isFetchingEditOffering, setIsFetchingEditOffering] = useState(false);
  const [isFetchingDefaultOffering, setIsFetchingDefaultOffering] = useState(
    false,
  );
  const [codeOfferingError, setCodeOfferingError] = useState({
    error: false,
    errorMessage: "",
  });

  const productName = useMemo(() => {
    return t(currentOffer?.product_line_code?.toLowerCase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOffer]);

  useEffect(() => {
    dispatch(fetchDataTrigger());

    return () => {
      dispatch(resetAll());
    };
  }, [dispatch]);

  const handleSort = (sort: TableHeaderSort) => {
    dispatch(fetchProductLinesRequest());
    dispatch(updateSort(sort));
  };

  const openDefaultOfferingModal = (offer: any) => {
    setCurrentOffer(offer);
    showShowDefaultOfferingModal(true);
  };

  const closeDefaultOfferingModal = () => {
    setCurrentOffer(null);
    showShowDefaultOfferingModal(false);
  };

  const setOfferingAsDefault = async () => {
    try {
      // BE require props in snake-case
      /* eslint-disable */
      const { product_line_code, code, name } = currentOffer;
      setIsFetchingDefaultOffering(true);
      await ProductLineAPIService.setDefault(product_line_code, code);

      toaster.success({
        content: t("offeringMadeDefaultSuccessfully", { offering: name }),
      });
      dispatch(fetchDataTrigger());
      dispatch(fetchProductLinesRequest());
    } finally {
      setIsFetchingDefaultOffering(false);
      closeDefaultOfferingModal();
    }
  };

  const renderExpandedRow = (
    row: ITableDataParams<OfferingType>["rowData"],
  ) => {
    if (!row) {
      return <div />;
    }
    const skus = row.skus
      .concat()
      .sort((a: SKUWrapper, b: SKUWrapper) => {
        if (a.rank > b.rank) {
          return 1;
        }

        if (a.rank < b.rank) {
          return -1;
        }

        return 0;
      })
      .map((item: SKUWrapper) => {
        return item.sku.sp_sku_code;
      });

    return (
      <ExpandedRow
        code={row.code}
        description={row.description}
        skus={skus}
        isDefault={!!row.isDefault}
        onMakeDefault={() => {
          openDefaultOfferingModal(row);
        }}
      />
    );
  };

  const openNewOfferingModal = () => {
    dispatch(fetchProductLinesRequest());
    setShowNewOfferingModal(true);
  };

  const closeNewOfferingModal = () => {
    setShowNewOfferingModal(false);
  };

  const openEditOfferingModal = (
    rowData: ITableDataParams<OfferingType>["rowData"],
  ) => {
    dispatch(fetchProductLinesRequest());
    setCurrentOffer(rowData);
    setShowEditOfferingModal(true);
  };

  const closeEditOfferingModal = () => {
    setCurrentOffer(null);
    setShowEditOfferingModal(false);
  };

  const getDateWithoutTime = (endsAtDate?: string) => {
    const initiateDate = (e?: string) => {
      return e ? new Date(e) : new Date();
    };

    const dataDependentDate = endsAtDate
      ? initiateDate(endsAtDate)
      : initiateDate();
    const options = { year: "numeric", month: "numeric", day: "numeric" };
    const parsedDate = dataDependentDate.toLocaleDateString("en-US", options);

    return initiateDate(parsedDate);
  };

  const onCreateOffering = (offering: IOfferingPayload) => {
    if (
      offering.ends_at &&
      getDateWithoutTime(offering.ends_at) < getDateWithoutTime()
    ) {
      setCurrentOffer(offering);
      openConfirmCreateModal();
      return;
    }

    createOffering(offering);
  };

  const onConfirmCreateOffering = () => {
    createOffering(currentOffer);
    closeConfirmCreateModal();
    setCurrentOffer(null);
  };

  const createOffering = async (offering: IOfferingPayload) => {
    try {
      setIsFetchingCreateOffering(true);
      await offeringService.create(offering);
      Toaster.success({
        content: t("offeringSavedSuccessfully", { name: offering.name }),
      });
      dispatch(fetchDataTrigger());
      closeNewOfferingModal();
    } catch (error) {
      if (
        error?.response?.data.type ===
        PROBLEM_TYPE_SKU_BEING_TO_ANOTHER_OFFERING
      ) {
        Toaster.error({
          content: t("SKUAreAlreadyInUse"),
        });
        return;
      }

      apiOfferingCodeErrorHandler(error);
      Toaster.error({
        content:
          codeOfferingError.errorMessage || error.response?.data?.error_message,
      });
    } finally {
      setIsFetchingCreateOffering(false);
    }
  };

  const onEditOffering = (offering: IOfferingPayload) => {
    setCurrentEditedOffer({ ...currentOffer, ...offering });
    if (
      offering.ends_at &&
      new Date(offering.ends_at).getTime() < new Date().getTime()
    ) {
      openConfirmEditModal();
      return;
    }

    editOffering(offering);
  };

  const onConfirmEditOffering = () => {
    editOffering(currentEditedOffer);
    closeConfirmEditModal();
  };

  const editOffering = async (offering: IOfferingPayload) => {
    const { code } = offering;
    try {
      setIsFetchingEditOffering(true);
      await offeringService.edit(code, {
        name: offering.name,
        description: offering.description,
        ends_at: offering.ends_at,
        sku_ids: offering.sku_ids,
      });
      Toaster.success({
        content: t("offeringUpdatedSuccessfully", { name: offering.name }),
      });
      dispatch(fetchDataTrigger());
      closeEditOfferingModal();
    } catch (error) {
      if (
        error?.response?.data.type ===
        PROBLEM_TYPE_SKU_BEING_TO_ANOTHER_OFFERING
      ) {
        Toaster.error({
          content: t("SKUAreAlreadyInUse"),
        });
        return;
      }

      if (error.response?.data?.error_code === "OfferingCodeAlreadyUsed") {
        setCodeOfferingError({
          error: true,
          errorMessage: t(error.response.data.error_code),
        });
        Toaster.error({
          content:
            t(error.response.data.error_code) ||
            error.response?.data?.error_message,
        });
      }
    } finally {
      setIsFetchingEditOffering(false);
    }
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const pageSize = parseInt(event.currentTarget.value);

    dispatch(updatePagination({ pageSize, page: 0 }));
  };

  const handlePageChange = (page: number) => {
    dispatch(updatePagination({ page }));
  };

  const onSKUUpdate = (params: ISKUParams) => {
    SKUAPIService.getAll(params).then(({ items }: ISKUResponse) => {
      const availableList = items.filter((item) => {
        return item.is_active;
      });
      setSKUList(sortBy(availableList, "sp_sku_code"));
    });
  };

  const clientOfferingCodeErrorHandler = useCallback(
    (code: string): void => {
      const isMatchRegExp = validationRules.capitalizeLetters_AndNumbers.test(
        code,
      );

      if (!isMatchRegExp) {
        setCodeOfferingError({
          error: true,
          errorMessage: t("offeringCodeNotMatchRegExp"),
        });
        return;
      }

      setCodeOfferingError({ error: false, errorMessage: "" });
    },
    [t, setCodeOfferingError],
  );

  const apiOfferingCodeErrorHandler = (apiError: any): void => {
    if (apiError.response?.data?.error_code === "OfferingCodeAlreadyUsed") {
      setCodeOfferingError({
        error: true,
        errorMessage: t(apiError.response.data.error_code),
      });
    }
  };

  return (
    <section className="offering">
      <div className="title-block-wrapper">
        <h1>{t("offerings")}</h1>

        <Can I={ACTION.WRITE} a={SUBJECT.PRODUCT}>
          <ButtonPrimary onClick={openNewOfferingModal}>
            {t("newOffering")}
          </ButtonPrimary>
        </Can>
      </div>

      <div className="mb-m">
        <FilterSection />
      </div>
      {isFetching ? (
        <ContentLoaderContainer />
      ) : error || !data.length ? (
        <Message>{t("nothingToDisplay")}</Message>
      ) : (
        <OfferingsTable
          data={data}
          defaultSort={defaultSort}
          onSort={handleSort}
          onEdit={openEditOfferingModal}
          renderExpandedRow={renderExpandedRow}
        />
      )}
      <Pagination
        page={currentPage}
        pages={pages}
        onPageSizeChange={handlePageSizeChange}
        onPageChange={handlePageChange}
      />

      <Can I={ACTION.WRITE} a={SUBJECT.PRODUCT}>
        <ConfirmModal
          title={t("confirmDefaultRecurlyOffering")}
          message={
            <Trans
              i18nKey="wouldLikeToMakeDefaultOffering"
              tOptions={{
                productCode: productName,
                offeringName: currentOffer?.name,
              }}
            >
              Are you sure you would like to make{" "}
              <strong>{currentOffer?.name}</strong> the Default Recurly Offering
              for {productName}?
            </Trans>
          }
          submitBtnText={t("makeDefaultOffering")}
          isLoading={isFetchingDefaultOffering}
          isOpen={showDefaultOfferingModal}
          onSubmit={setOfferingAsDefault}
          onDismiss={closeDefaultOfferingModal}
        />

        {showNewOfferingModal ? (
          <OfferingModal
            isOpen={showNewOfferingModal}
            SKUList={SKUList}
            productLines={productLines}
            tenantCode={tenantCode}
            isLoading={isFetchingCreateOffering}
            onConfirm={onCreateOffering}
            onDismiss={closeNewOfferingModal}
            onSKUUpdate={onSKUUpdate}
            onClientError={clientOfferingCodeErrorHandler}
            codeOfferingError={codeOfferingError}
          />
        ) : null}

        {isShowConfirmCreateModal ? (
          <ConfirmModal
            title={t("theOfferingEndDateHasPassed")}
            submitBtnText={t("ok")}
            isOpen={isShowConfirmCreateModal}
            onSubmit={onConfirmCreateOffering}
            onDismiss={closeConfirmCreateModal}
          />
        ) : null}

        {showEditOfferingModal ? (
          <OfferingModal
            title={updateOfferingText}
            confirmText={updateOfferingText}
            isOpen={showEditOfferingModal}
            offering={currentOffer}
            SKUList={SKUList}
            productLines={productLines}
            tenantCode={tenantCode}
            isLoading={isFetchingEditOffering}
            onConfirm={onEditOffering}
            onDismiss={closeEditOfferingModal}
            onSKUUpdate={onSKUUpdate}
            onClientError={clientOfferingCodeErrorHandler}
            codeOfferingError={codeOfferingError}
          />
        ) : null}

        {isShowConfirmEditModal ? (
          <ConfirmModal
            title={t("theOfferingEndDateHasPassed")}
            submitBtnText={t("ok")}
            isOpen={isShowConfirmEditModal}
            onSubmit={onConfirmEditOffering}
            onDismiss={closeConfirmEditModal}
          />
        ) : null}
      </Can>
    </section>
  );
};
